import React from 'react';
import { motion } from 'framer-motion';
import { RevenueProgressCircleProps } from '@/types/revenue';

export const RevenueProgressCircle: React.FC<RevenueProgressCircleProps> = ({
  percentage,
  size = 'md'
}) => {
  const clampedPercentage = isNaN(percentage) ? 0 : Math.min(Math.max(percentage, 0), 100);

  // Size configurations
  const sizeConfig = {
    sm: { width: 60, height: 60, strokeWidth: 4, fontSize: 'text-xs' },
    md: { width: 100, height: 100, strokeWidth: 6, fontSize: 'text-lg' },
    lg: { width: 140, height: 140, strokeWidth: 8, fontSize: 'text-2xl' }
  };

  const config = sizeConfig[size];
  const radius = (config.width - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (clampedPercentage / 100) * circumference;

  // Color coding
  const getColor = () => {
    if (clampedPercentage < 70) return '#ef4444'; // red
    if (clampedPercentage < 90) return '#f59e0b'; // yellow
    return '#10b981'; // green
  };

  const color = getColor();

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg
        width={config.width}
        height={config.height}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={config.width / 2}
          cy={config.height / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth={config.strokeWidth}
          fill="none"
        />

        {/* Progress circle */}
        <motion.circle
          cx={config.width / 2}
          cy={config.height / 2}
          r={radius}
          stroke={color}
          strokeWidth={config.strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </svg>

      {/* Percentage text */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.span
          className={`font-bold ${config.fontSize}`}
          style={{ color }}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.3 }}
        >
          {Math.round(clampedPercentage)}%
        </motion.span>
      </div>
    </div>
  );
};
